import React, { useState, useCallback, useEffect, useRef } from "react";
import { debounce } from "lodash";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Info } from "@phosphor-icons/react";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { INote } from "@/utils/Types";
import { useForm } from "react-hook-form";
import { updateNoteFieldById } from "../utils/Services";

interface ScratchpadModalProps {
  note: INote;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const ScratchpadModal: React.FC<ScratchpadModalProps> = ({
  note,
  isOpen,
  setIsOpen,
}) => {
  const canUpdate = useRef(false);

  const values = note;

  const {
    register,
    watch,
    formState: { errors },
  } = useForm<INote>({ values });

  const autoSave = (value: INote) => {
    if (!canUpdate.current) {
      canUpdate.current = true;
      return;
    }

    updateNoteFieldById("notes", value.id, {
      scratchpad: value.scratchpad,
    });
    canUpdate.current = false;
  };

  const debounceFn = useCallback(debounce(autoSave, 1000), []);

  useEffect(() => {
    const subscription = watch((value) => {
      // @ts-ignore
      debounceFn(value);
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    canUpdate.current = false;
  }, [note]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="w-full sm:max-w-[600px] dark:bg-gray-900">
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center">
              <span className="ml-2">Scratchpad</span>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col p-4 max-h-[70vh] overflow-auto">
          <div className="my-2">
            <div className="flex items-center">
              <Info className="mr-1" size={18} />
              <span className="text-sm">
                It will not be included in the final note.
              </span>
            </div>
            <Textarea
              id="scratchpad"
              placeholder="Add text here"
              className="mt-2 overflow-hidden dark:text-gray-200 border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-background"
              {...register("scratchpad")}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScratchpadModal;
